import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const NAVY = [0, 48, 87];
const GREEN = [120, 190, 33];

// Snapshot a DOM element into a multi-page A4 PDF and download it.
export async function downloadElementPdf(el, filename) {
  if (!el) return;
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    windowWidth: el.scrollWidth,
    onclone: (doc) => {
      doc.querySelectorAll("[data-pdf-hide]").forEach((n) => { n.style.visibility = "hidden"; });
    },
  });
  const img = canvas.toDataURL("image/jpeg", 0.92);
  const pdf = new jsPDF("p", "pt", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgW = pageW;
  const imgH = (canvas.height * imgW) / canvas.width;
  let heightLeft = imgH;
  let position = 0;
  pdf.addImage(img, "JPEG", 0, position, imgW, imgH);
  heightLeft -= pageH;
  while (heightLeft > 0) {
    position -= pageH;
    pdf.addPage();
    pdf.addImage(img, "JPEG", 0, position, imgW, imgH);
    heightLeft -= pageH;
  }
  pdf.save(filename);
}

// Build a clean, branded Candidate Fit PDF from structured data.
export function downloadFitPdf(job, a) {
  const pdf = new jsPDF("p", "pt", "a4");
  const W = pdf.internal.pageSize.getWidth();
  const M = 48;

  pdf.setFillColor(...NAVY);
  pdf.rect(0, 0, W, 92, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.text("GPA+ Candidate Fit Report", M, 46);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(168, 224, 99);
  pdf.text(`${job.title || "Role"}  ·  ${job.company || ""}`, M, 68);

  let y = 128;
  pdf.setTextColor(...NAVY);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text(a.student_name || "Candidate", M, y);
  y += 20;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(90, 90, 90);
  pdf.text(`Overall fit: ${a.match ?? 0}%     TSB ${a.tsb ?? "—"}  (${a.tsb_100 ?? "—"}/100)`, M, y);
  y += 30;

  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...NAVY);
  pdf.setFontSize(10);
  pdf.text("Rank", M, y);
  pdf.text("Transferable skill", M + 46, y);
  pdf.text("Score", M + 250, y);
  pdf.text("Band", M + 312, y);
  pdf.text("Fit", M + 430, y);
  y += 8;
  pdf.setDrawColor(210);
  pdf.line(M, y, W - M, y);
  y += 16;

  pdf.setFont("helvetica", "normal");
  (a.skill_breakdown || []).forEach((s) => {
    if (y > 770) { pdf.addPage(); y = 60; }
    const tag = s.strong ? "Strength" : s.gap ? "Gap" : "OK";
    if (s.strong) pdf.setTextColor(16, 155, 105);
    else if (s.gap) pdf.setTextColor(217, 119, 6);
    else pdf.setTextColor(40, 40, 40);
    pdf.text(String(s.rank), M, y);
    pdf.text(String(s.skill || ""), M + 46, y);
    pdf.text(Number(s.score).toFixed(1), M + 250, y);
    pdf.text(String(s.band || ""), M + 312, y);
    pdf.text(`${s.pct}%  ${tag}`, M + 430, y);
    y += 18;
  });

  y += 22;
  pdf.setDrawColor(...GREEN);
  pdf.line(M, y - 12, W - M, y - 12);
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(
    "Ranking reflects this role's skill priority. Figures are illustrative and computed on GPA+ mock data; not a hiring decision.",
    M, y, { maxWidth: W - 2 * M }
  );

  pdf.save(`GPA+ Fit - ${(a.student_name || "candidate").replace(/[^\w -]/g, "")}.pdf`);
}

const RIASEC_RGB = {
  Realistic: [194, 65, 12], Investigative: [29, 78, 216], Artistic: [124, 58, 237],
  Social: [5, 150, 105], Enterprising: [185, 28, 28], Conventional: [8, 145, 178],
};

// Build a clean, branded Career Interest (RIASEC) PDF from structured O*NET data.
export function downloadInterestPdf(result, name) {
  const pdf = new jsPDF("p", "pt", "a4");
  const W = pdf.internal.pageSize.getWidth();
  const M = 48;

  pdf.setFillColor(...NAVY);
  pdf.rect(0, 0, W, 92, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.text("GPA+ Career Interest Profile", M, 46);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(168, 224, 99);
  pdf.text("O*NET Interest Profiler (RIASEC)", M, 68);

  let y = 128;
  pdf.setTextColor(...NAVY);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text(name ? `${name}` : "Career Interests", M, y);
  y += 18;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(120, 120, 120);
  if (result.created_at) pdf.text(`Completed ${result.created_at.slice(0, 10)}`, M, y);
  y += 26;

  const top3 = result.top3 || [];
  if (top3.length) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...NAVY);
    pdf.text("Top interest areas", M, y);
    y += 16;
    pdf.setFontSize(10);
    top3.forEach((r, i) => {
      const rgb = RIASEC_RGB[(r.title || "").trim()] || [90, 90, 90];
      pdf.setTextColor(...rgb);
      pdf.setFont("helvetica", "bold");
      pdf.text(`#${i + 1}  ${r.title}`, M, y);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(90, 90, 90);
      pdf.text(`score ${r.score}`, M + 200, y);
      y += 16;
    });
    y += 12;
  }

  const riasec = result.riasec || [];
  if (riasec.length) {
    const maxScore = Math.max(1, ...riasec.map((r) => r.score));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...NAVY);
    pdf.text("All six interest areas", M, y);
    y += 16;
    const barW = W - 2 * M - 60;
    riasec.forEach((r) => {
      const rgb = RIASEC_RGB[(r.title || "").trim()] || [120, 120, 120];
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(50, 50, 50);
      pdf.text(r.title, M, y);
      pdf.setFillColor(235, 238, 242);
      pdf.rect(M + 130, y - 8, barW - 130, 8, "F");
      pdf.setFillColor(...rgb);
      pdf.rect(M + 130, y - 8, (barW - 130) * (r.score / maxScore), 8, "F");
      pdf.setTextColor(90, 90, 90);
      pdf.text(String(r.score), M + barW + 6, y);
      y += 18;
    });
    y += 14;
  }

  const careers = result.careers || [];
  if (careers.length) {
    if (y > 680) { pdf.addPage(); y = 60; }
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...NAVY);
    pdf.text("Careers to explore", M, y);
    y += 16;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    careers.slice(0, 15).forEach((c) => {
      if (y > 780) { pdf.addPage(); y = 60; }
      pdf.setTextColor(40, 40, 40);
      pdf.text(`•  ${c.title}${c.bright_outlook ? "  (Bright Outlook)" : ""}`, M, y);
      y += 15;
    });
    y += 10;
  }

  if (y > 740) { pdf.addPage(); y = 60; }
  pdf.setDrawColor(...GREEN);
  pdf.line(M, y - 10, W - M, y - 10);
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(
    "Interests are separate from your Transferable Skills Benchmark and are not a measure of ability. Scores and career matches are provided by the O*NET Web Services (USDOL/ETA). O*NET® is a trademark of USDOL/ETA.",
    M, y, { maxWidth: W - 2 * M }
  );

  pdf.save(`GPA+ Career Interests${name ? " - " + name.replace(/[^\w -]/g, "") : ""}.pdf`);
}
